import { User } from 'oidc-client-ts';

import { getEnvVariables } from '../../services/optionsService';
import { FEDERATION_GRANT_TYPE } from '../../session/constants';

import { getOIDCUserStorageKey } from './getOIDCUserManagerSettings';
import { getExpirationTime, loadOIDCAuthInfo, type OIDCAuthInfo } from './oidcAuthInfo';
import { exchangeOIDCAuthInfo, revokeExchangeAccessToken } from './oidcAuthInfoExchange';
import { loadSSOConfig } from './ssoConfig';

// FIXME: this is only to be used in i18n for now, because it doesn't support async config
export function getOIDCAuthInfoFromStorageSync(): OIDCAuthInfo | null {
  const { ssoAuthority, ssoClientId } = getEnvVariables();

  if (!ssoAuthority || !ssoClientId) {
    return null;
  }

  const ssoConfig = loadSSOConfig();

  if (!ssoConfig?.client_id) {
    return null;
  }

  const userStorageKey = getOIDCUserStorageKey(ssoConfig);
  if (!userStorageKey) {
    return null;
  }

  const storageUser = localStorage.getItem(userStorageKey);

  if (!storageUser) {
    return null;
  }

  const user = User.fromStorageString(storageUser);

  if (ssoConfig.grant_type === 'token') {
    return {
      accessToken: user.access_token,
      expirationTime: getExpirationTime(user.expires_in || 0),
      instanceUid: user.profile.instance_uid as string,
    };
  }

  if (ssoConfig.grant_type === FEDERATION_GRANT_TYPE) {
    return loadOIDCAuthInfo(ssoAuthority, ssoClientId);
  }

  throw new Error('Unknown grant type');
}

let exchangeOIDCAuthInfoPromise: Promise<OIDCAuthInfo> | null = null;

export async function getOIDCAuthInfoFromStorage(): Promise<OIDCAuthInfo | null> {
  const { ssoAuthority, ssoClientId } = getEnvVariables();

  if (!ssoAuthority || !ssoClientId) {
    return null;
  }

  const ssoConfig = loadSSOConfig();

  if (!ssoConfig?.client_id) {
    return null;
  }

  const userStorageKey = getOIDCUserStorageKey(ssoConfig);
  if (!userStorageKey) {
    return null;
  }

  const storageUser = localStorage.getItem(userStorageKey);

  if (!storageUser) {
    return null;
  }

  const user = User.fromStorageString(storageUser);

  if (ssoConfig.grant_type === 'token') {
    return {
      accessToken: user.access_token,
      expirationTime: getExpirationTime(user.expires_in || 0),
      instanceUid: user.profile.instance_uid as string,
    };
  }

  if (!user.id_token) {
    return null;
  }

  if (ssoConfig.grant_type === FEDERATION_GRANT_TYPE) {
    if (exchangeOIDCAuthInfoPromise) {
      return await exchangeOIDCAuthInfoPromise;
    }

    exchangeOIDCAuthInfoPromise = exchangeOIDCAuthInfo(user.id_token);

    try {
      return await exchangeOIDCAuthInfoPromise;
    } finally {
      exchangeOIDCAuthInfoPromise = null;
    }
  }

  throw new Error('Unknown grant type');
}

export async function revokeOIDCAuthInfo(): Promise<void> {
  const { ssoAuthority, ssoClientId } = getEnvVariables();
  if (!ssoAuthority || !ssoClientId) {
    return;
  }

  const ssoConfig = loadSSOConfig();
  if (!ssoConfig) {
    return;
  }

  if (ssoConfig.grant_type !== FEDERATION_GRANT_TYPE) {
    return;
  }

  const oidcInfo = getOIDCAuthInfoFromStorageSync();
  if (!oidcInfo) {
    return;
  }

  await revokeExchangeAccessToken(ssoAuthority, ssoClientId, oidcInfo.accessToken);
}
